import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, CalendarCheck } from "lucide-react";
import { GoldTrophy } from "../components/GoldTrophy";
import { useConfettiOnView } from "../hooks/useConfettiOnView";
import { PageMeta } from "../components/PageMeta";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { CtaBand } from "../components/CtaBand";
import { StatsBand } from "../components/StatsBand";
import { TestimonialCarousel } from "../components/TestimonialCarousel";
import { LogoMarquee } from "../components/LogoMarquee";
import { VideoSection } from "../components/VideoSection";
import { AudienceCards } from "../components/AudienceCards";
import { ReviewBadge } from "../components/ReviewBadge";
import { Pic } from "../components/Pic";
import { HeroCentered } from "../components/HeroCentered";
import { InquiryCTA } from "../components/PlanEventModal";
import { HEADLINE, HERO_EYEBROW, HERO_INTRO } from "../data/headline";
import { PACKAGES, TESTIMONIALS } from "../data/content";

// Hero layout experiment: "centered" (floating icons + polaroids) or "side" (original photo side-by-side). Flip to revert.
export const HERO_LAYOUT = "centered";

const fade = (d) => ({ initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, delay: d, ease: [0.22, 1, 0.36, 1] } });

const WHY = [
  { icon: Sparkles, title: "Leadership & Corporate Events", desc: "Game-based leadership sessions and large-scale corporate events and conferences." },
  { icon: CalendarCheck, title: "Full-Service Event Planning", desc: "From first idea to final applause, we handle every detail." },
  { icon: GoldTrophy, title: "Fun You'll Never Forget", desc: "Engaging games and friendly competition that strengthen your culture." },
];

const HeroSideBySide = () => (
      <section data-testid="home-hero" className="relative isolate overflow-hidden bg-white">
        <div aria-hidden className="pointer-events-none absolute inset-0 -z-10" style={{ background: "linear-gradient(to bottom, #fff 0%, #fff 60%, rgba(255,194,14,0.08) 75%, rgba(255,194,14,0.35) 100%)" }} />
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 pb-16 pt-14 sm:px-8 lg:min-h-[82vh] lg:grid-cols-12 lg:gap-14 lg:py-20">
          <div className="lg:col-span-6">
            <motion.p {...fade(0)} data-testid="hero-eyebrow" className="eyebrow mb-5 md:whitespace-nowrap">{HERO_EYEBROW}</motion.p>
            <h1 data-testid="hero-headline" className="font-display text-4xl leading-[1.02] text-black sm:text-5xl lg:text-6xl xl:text-7xl">
              {HEADLINE.line1} <span className="text-en-teal">{HEADLINE.line2}</span>
            </h1>
            <motion.p {...fade(0.2)} data-testid="hero-intro" className="mt-7 max-w-xl text-base leading-relaxed text-black/70 sm:text-lg lg:max-w-none">
              {HERO_INTRO.line1}{" "}<br className="hidden lg:inline" />
              {HERO_INTRO.line2}
            </motion.p>
            <motion.div {...fade(0.3)} className="mt-10 flex flex-wrap items-center gap-4">
              <InquiryCTA label="Plan Your Event" icon data-testid="hero-plan-event-btn" className="btn-primary !px-8 !py-4" />
              <Link to="/experiences" data-testid="hero-explore-btn" data-track="explore_experiences" className="btn-outline !px-8 !py-4">Explore Experiences</Link>
            </motion.div>
            <motion.div {...fade(0.4)} className="mt-8">
              <ReviewBadge testId="hero-review-badge" />
            </motion.div>
          </div>
          <div className="relative lg:col-span-6">
            <Pic src="/images/event-1.jpg" alt="Corporate team seated around a table playing live trivia at an Entertainment Nation event" priority sizes="(min-width: 1024px) 50vw, 100vw" data-testid="hero-photo" className="aspect-[4/3] w-full rounded-3xl border border-black/10 object-cover shadow-[0_40px_80px_-30px_rgba(0,0,0,0.45)] lg:aspect-[5/4]" />
            <img src="/images/w/blob-1-480.webp" alt="" aria-hidden width="468" height="480" className="absolute -bottom-8 -left-6 w-36 animate-float drop-shadow-2xl sm:w-44 lg:-left-12" />
          </div>
        </div>
      </section>
);

export default function Home() {
  const trophyRef = useRef(null);
  useConfettiOnView(trophyRef);
  return (
    <>
      <PageMeta title="Live Game Experiences That Bring People Together" description="Entertainment Nation delivers live-hosted trivia, game shows and fully produced events for corporate teams, country clubs, communities, charities and private celebrations." />

      {HERO_LAYOUT === "centered" ? <HeroCentered /> : <HeroSideBySide />}

      <LogoMarquee />
      <VideoSection />

      <section className="bg-white py-20 lg:py-28" data-testid="home-what-we-do">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:items-center lg:gap-20">
          <div className="lg:col-span-7">
            <Reveal className="max-w-3xl">
              <p className="eyebrow mb-4">What we do</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-black">We create experiences that bring people together.</h2>
              <div className="mt-6 max-w-[55ch] space-y-4 text-base leading-[1.7] text-black/70 sm:text-lg">
                <p>From high-impact corporate events to elevated private celebrations, we deliver fully produced experiences that engage, energize and connect your audience.</p>
                <p>Top-tier hosts, dynamic game formats and seamless production — tailored to your goals, brand and audience.</p>
              </div>
            </Reveal>
            <ul className="mt-10 max-w-[60ch] divide-y divide-black/10" data-testid="what-we-do-list">
              {WHY.map((w, i) => (
                <Reveal key={w.title} delay={0.12 * i} as="li" className="flex items-start gap-5 py-6 first:pt-0 last:pb-0">
                  <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${i === 2 ? "bg-en-yellow/15" : "bg-en-teal/10"}`}>
                    <w.icon className={`h-6 w-6 ${i === 2 ? "" : "text-en-teal"}`} data-testid={`why-icon-${i}`} />
                  </span>
                  <span>
                    <h3 className="font-display text-xl leading-snug text-black sm:text-[22px]">{w.title}</h3>
                    <p className="mt-1.5 text-base leading-[1.7] text-black/65 sm:text-lg">{w.desc}</p>
                  </span>
                </Reveal>
              ))}
            </ul>
            <p className="mt-10 text-sm text-black/60">Learn more <Link to="/about" data-testid="home-about-link" className="link-teal">about the team behind Entertainment Nation</Link> or see <Link to="/who-we-serve" data-testid="home-serve-link" className="link-teal">who we work with</Link>.</p>
          </div>
          <div className="order-first lg:order-none lg:col-span-5">
            <Pic imgRef={trophyRef} src="/images/blob-2.png" alt="Winning team pointing at the trophy after a Trivia Nation game show" sizes="(min-width: 1024px) 40vw, 100vw" data-testid="what-we-do-photo" className="mx-auto w-full max-w-md" />
          </div>
        </div>
      </section>

      <section className="relative bg-white border-t border-black/10 py-20 lg:py-28" data-testid="home-experiences">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <SectionHeading eyebrow="Our experiences" title="Pick a show. We handle the rest." intro="Turnkey game packages with top-tier talent and seamless production — or a fully custom event built around your people." />
            <Reveal delay={0.1}><Link to="/experiences" data-testid="home-experiences-link" className="btn-outline">See all experiences <ArrowRight className="h-4 w-4" /></Link></Reveal>
          </div>
          <div className="mt-12 grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3 lg:grid-cols-6">
            {PACKAGES.map((p, i) => (
              <Reveal key={p.name} delay={0.06 * i}>
                <Link to="/experiences" data-testid={`home-package-card-${i}`} data-track="package_card" className="group block overflow-hidden rounded-xl border border-black/10 bg-white shadow-[0_10px_30px_-18px_rgba(0,0,0,0.3)] card-lift">
                  <div className="aspect-[2/3] overflow-hidden">
                    <Pic src={p.image} alt={`${p.name} show poster`} sizes="(min-width: 1024px) 16vw, (min-width: 768px) 33vw, 50vw" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <p className="font-gothic p-3 text-center uppercase tracking-wider text-xs text-black/80">{p.name}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <AudienceCards />

      <section className="bg-white py-20 lg:py-28" data-testid="home-testimonials">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="flex justify-center"><GoldTrophy className="h-9 w-9 mb-5" data-testid="home-testimonials-trophy" /></Reveal>
          <SectionHeading align="center" eyebrow="Don't just take our word for it" title="Hear from our clients." />
          <Reveal delay={0.1} className="mt-10"><ReviewBadge size="hero" testId="home-review-proof" /></Reveal>
          <Reveal delay={0.15} className="mt-12"><TestimonialCarousel items={TESTIMONIALS} /></Reveal>
        </div>
      </section>

      <StatsBand />


      <CtaBand testId="home-cta" text="Elevating events into unforgettable experiences — partner with us to bring your vision to life." />
    </>
  );
}
